import { computed, unref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { parseStoredDocument } from '../utils/document.utils';
import { httpClient, throwIfError } from '@/common/api/http-client';
import type { ApiResponse } from '@/common/api/models.pleased';
import * as env from '@/common/environment';
import { StoredDocument } from '@pleased-npm/fe-customisation';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

/** Vue 2.7: no `MaybeRefOrGetter` / `toValue`; `unref` unwraps refs and leaves booleans as-is. */
type CustomisationDocumentsEnabled = boolean | Ref<boolean> | ComputedRef<boolean>;

/**
 * Fetch all customisation documents from the API
 * Returns raw StoredDocument array from the API
 */
const fetchDocuments = async (): Promise<StoredDocument[]> => {
  let brandCode = undefined;
  try {
    brandCode = httpClient.defaults.headers['brand-subdomain'] || env.get('brandCode');
    const response = await httpClient.get<ApiResponse<StoredDocument[]>>(`/api/v1/chat-widget/documents/${brandCode}`);
    throwIfError(response);
    return response.data.data;
  } catch (error) {
    logger.error('[CUSTOMISATION_API] Error fetching customisation documents', {
      error: parseErrorMessage(error),
      brandCode,
    });
    throw error;
  }
};

/**
 * TanStack Query hook to fetch all customisation documents
 * Uses select to transform StoredDocument[] to Document[]
 * Pure data fetching - consumers decide what to do with the data
 *
 * @param enabled - When false, the query does not run (defaults to true).
 *                  Avoid `initialData: []` here: it marks the query successful immediately and keeps `isLoading` false.
 */
export const useCustomisationDocuments = (enabled?: CustomisationDocumentsEnabled) => {
  const queryEnabled = computed<boolean>(() => {
    if (enabled === undefined) {
      return true;
    }
    return unref(enabled) as boolean;
  });

  return useQuery({
    queryKey: ['customisation', 'documents'],
    queryFn: fetchDocuments, // TODO: remove this when the API is fixed
    select: (storedDocuments) => storedDocuments.map(parseStoredDocument),

    enabled: queryEnabled,
    staleTime: Infinity,

    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
